import { TF } from 'react-translator-component';

import { createDoorAccessMock } from './doorAccess.mock';
import { DoorAccessApi, DoorAccessError } from './doorAccess.types';

import {
  createDocument,
  createDocumentRequest,
  updateDocumentByPath,
} from '../services/documents';
import { messageForStatus } from '../services/unwrapResult';
import {
  DoorAttemptResult,
  DoorConfig,
  DoorOpening,
  DoorSummary,
} from '../types/responses/DoorAccess';

const connectorPath = (chargeBoxPk: string | number, connectorId: number) =>
  `chargepoints/${chargeBoxPk}/connector/${connectorId}/door`;

interface DocumentRequestResult {
  type?: 'success' | 'error';
  status?: number;
  data?: any;
}

const unwrap = <T>(response: unknown): T => {
  const result = response as DocumentRequestResult;

  if (!result || !result.type || typeof result.status !== 'number') {
    throw new DoorAccessError(messageForStatus(null));
  }

  if (result.type === 'error' || result.status >= 400) {
    throw new DoorAccessError(
      result.data?.error
        ? TF(result.data.error)
        : messageForStatus(result.status),
    );
  }

  if (result.data?.isJSON === false) {
    throw new DoorAccessError(messageForStatus(null));
  }

  return result.data as T;
};

const getDoor = async <T>(entity: string): Promise<T> =>
  unwrap<T>(await createDocumentRequest(entity, { method: 'GET' }));

const postDoor = async <T>(entity: string, payload?: unknown): Promise<T> =>
  unwrap<T>(await createDocument(payload, entity));

const putDoor = async <T>(entity: string, payload: unknown): Promise<T> =>
  unwrap<T>(await updateDocumentByPath(payload, entity));

export const httpDoorAccessApi: DoorAccessApi = {
  getSummary: (pk) =>
    getDoor<{ list?: DoorSummary[] }>(`chargepoints/${pk}/door`).then((data) =>
      Array.isArray(data?.list) ? data.list : [],
    ),
  getConfig: (pk, id) => getDoor<DoorConfig>(connectorPath(pk, id)),
  saveConfig: (pk, id, payload) =>
    putDoor<DoorConfig>(connectorPath(pk, id), payload),
  testConfig: (pk, id, payload) =>
    postDoor<DoorAttemptResult>(`${connectorPath(pk, id)}/test`, payload),
  open: (pk, id, payload) =>
    postDoor<DoorAttemptResult>(`${connectorPath(pk, id)}/open`, payload),
  getOpenings: (pk, id) =>
    getDoor<{ list?: DoorOpening[] }>(`${connectorPath(pk, id)}/openings`).then(
      (data) => (Array.isArray(data?.list) ? data.list : []),
    ),
};

const isDoorAccessMockEnabled = (): boolean =>
  process.env.REACT_APP_DOOR_ACCESS_MOCK === 'true';

export const doorAccessApi: DoorAccessApi = isDoorAccessMockEnabled()
  ? createDoorAccessMock({ latencyMs: 400 })
  : httpDoorAccessApi;

export const doorErrorMessage = (error: unknown): string =>
  error instanceof DoorAccessError ? error.message : messageForStatus(null);
